<template>
    <div>
        <h1 class="text-center text-3xl text-theme-primary">
            Productos pendientes por despachar
        </h1>
        <div class="mt-4 px-4">
            <p class="text-justify">
                En esta sección encontras el detalle de los productos con
                referencia que estan pendientes por ser entregados, es decir los
                productos de las ordenes que estan en curso.
            </p>
        </div>

        <div class="my-3">
            <orders-filters
                v-model="query"
                :enabled-filters="$data.$enabledFilters"
                @enter="reloadItems"
            />
            <div class="flex justify-center mt-3">
                <app-button v-show="query" relief @click="reloadItems">
                    Consultar 🔎
                </app-button>
            </div>
        </div>
        <div class="w-full mt-4">
            <div class="flex justify-between flex-col lg:flex-row my-2">
                <div class="flex justify-around">
                    <tooltip-button
                        v-if="itemsCurrent.length > 0"
                        icon="📕"
                        message="Descargar resultado PDF"
                        @click="downloadPDFComponent"
                    />
                    <tooltip-button
                        v-if="itemsCurrent.length > 0"
                        icon="⏬"
                        message="Descargar resultado excel"
                        @click="downloadExcelComponent"
                    />
                    <tooltip-button
                        v-show="selected.length"
                        icon="👀"
                        message="Vista previa de órdenes seleccionadas"
                        @click="openPreview"
                    />
                </div>
                <div class="flex items-end justify-around"></div>
            </div>
            <custom-table
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                responsive
                outlined
                no-border-collapse
                hover
                select-mode="multi"
                selectable
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra referencias para entregar"
                @flex-selected="onRowSelected"
            >
                <template #cell(id)="data">
                    <a
                        v-if="data.item.id"
                        class="bg-stack-secondary text-white p-2 rounded-full"
                        :href="`/app/products/detail/${data.item.productId}?reference=${data.item.id}&tab=references`"
                        target="_blank"
                        rel="noopener"
                    >
                        <small>
                            {{
                                data.item.barcode
                                    ? `Cód. ${data.item.barcode}`
                                    : `ID ${data.item.id}`
                            }}
                        </small>
                    </a>
                    <small
                        v-else
                        class="bg-stack-secondary text-white p-2 rounded-full"
                    >
                        Producto Anónimo
                    </small>
                </template>
                <template #cell(productId)="data">
                    <a
                        v-if="data.item.id"
                        class="bg-stack-primary text-white p-2 rounded-full"
                        :href="`/app/products/detail/${data.item.productId}`"
                        target="_blank"
                        rel="noopener"
                    >
                        <small>
                            {{
                                data.item.sku
                                    ? `SKU ${data.item.sku}`
                                    : `ID ${data.item.productId}`
                            }}
                        </small>
                    </a>
                    <small
                        v-else
                        class="bg-stack-primary text-white p-2 rounded-full"
                    >
                        N/A
                    </small>
                </template>

                <template #cell(name)="data">
                    <span class="text-center">
                        {{ data.item.name }}
                    </span>
                </template>
                <template #cell(size)="data">
                    <div class="flex flex-col">
                        <span class="text-center capitalize">
                            {{
                                [data.item.size, data.item.color]
                                    .filter(Boolean)
                                    .join(" / ")
                            }}
                        </span>
                        <small
                            v-if="data.item.customReferenceValue"
                            class="text-theme-muted capitalize text-center"
                        >
                            {{
                                data.item.customReferenceTitle
                                    ? `${data.item.customReferenceTitle}: `
                                    : ""
                            }}{{ data.item.customReferenceValue }}
                        </small>
                    </div>
                </template>
                <template #cell(priceOffer)="data">
                    <span class="text-center">
                        {{ data.item.priceOffer | money }}
                    </span>
                </template>
                <template #cell(ordersNumber)="data">
                    <span class="text-center text-theme-primary">
                        {{ data.item.ordersNumber }}
                    </span>
                </template>
                <template #cell(quantity)="data">
                    <span class="text-center text-theme-primary">
                        {{ data.item.quantity }}
                    </span>
                </template>
                <template #cell(preview)="data">
                    <div class="flex justify-center">
                        <tooltip-button
                            icon="👀"
                            message="Vista previa de órdenes"
                            @click="openReferencePreview(data.item)"
                        />
                    </div>
                </template>
            </custom-table>
        </div>

        <app-dialog v-model="isOpenOrdersPreview" size="full">
            <template #header>
                <div class="flex justify-between w-full px-4">
                    <div></div>
                    <div
                        class="mx-4 mt-2 flex justify-center items-center flex-col"
                    >
                        <h3 class="text-xl text-theme-primary font-bold">
                            Vista previa de productos seleccionados
                        </h3>
                        <p class="text-sm mt-1">
                            {{ selected.length }} referencia{{
                                selected.length !== 1 ? "s" : ""
                            }}
                            seleccionada{{ selected.length !== 1 ? "s" : "" }}
                        </p>
                    </div>
                    <div class="flex gap-2">
                        <app-button
                            :loading="isGeneratingPDF"
                            @click="downloadPDFComponent"
                        >
                            📄 PDF
                        </app-button>
                        <app-button @click="downloadExcelComponent">
                            📊 Excel
                        </app-button>
                    </div>
                </div>
            </template>

            <div class="px-4">
                <div>
                    <div
                        v-for="reference in selected"
                        :key="reference.id"
                        class="mb-6 border rounded-lg p-4 shadow-sm"
                    >
                        <div class="flex items-start gap-4 mb-3 border-b pb-3">
                            <div class="flex-1">
                                <h4
                                    class="text-lg font-bold text-theme-primary mb-2"
                                >
                                    {{ reference.name }}
                                </h4>
                                <div class="grid grid-cols-2 gap-2 text-sm">
                                    <div v-if="reference.sku">
                                        <span class="font-semibold">
                                            SKU:
                                        </span>
                                        {{
                                            reference.sku || reference.productId
                                        }}
                                    </div>
                                    <div v-if="reference.id">
                                        <span class="font-semibold">
                                            Ref. ID:
                                        </span>
                                        {{ reference.id }}
                                    </div>
                                    <div>
                                        <span class="font-semibold">
                                            Código:
                                        </span>
                                        {{ reference.barcode || "N/A" }}
                                    </div>
                                    <div>
                                        <span class="font-semibold">
                                            Variante:
                                        </span>
                                        <span class="capitalize">
                                            {{
                                                [
                                                    reference.size,
                                                    reference.color
                                                ]
                                                    .filter(Boolean)
                                                    .join(" / ") || "N/A"
                                            }}
                                        </span>
                                        <span
                                            v-if="
                                                reference.customReferenceValue
                                            "
                                            class="capitalize block"
                                        >
                                            <span class="font-semibold">{{
                                                reference.customReferenceTitle
                                                    ? `${reference.customReferenceTitle}: `
                                                    : ""
                                            }}</span
                                            >{{
                                                reference.customReferenceValue
                                            }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="font-semibold">
                                            Cantidad:
                                        </span>
                                        <span class="text-theme-primary">
                                            {{ reference.quantity }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <div
                                    class="text-2xl font-bold text-theme-primary"
                                >
                                    {{ reference.priceOffer | currency }}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h5
                                class="text-sm font-semibold mb-2 text-theme-base"
                            >
                                Órdenes ({{ reference.ordersNumber || 0 }})
                            </h5>

                            <div
                                v-if="
                                    reference.orders &&
                                    reference.orders.length > 0
                                "
                                class="overflow-x-auto"
                            >
                                <table class="w-full text-sm border-collapse">
                                    <thead>
                                        <tr>
                                            <th
                                                class="px-2 py-1 text-left border"
                                            >
                                                ID
                                            </th>
                                            <th
                                                class="px-2 py-1 text-left border w-28"
                                            >
                                                Unidades <br />
                                                en la orden
                                            </th>
                                            <th
                                                class="px-2 py-1 text-left border"
                                            >
                                                Fecha Creación
                                            </th>
                                            <th
                                                class="px-2 py-1 text-left border"
                                            >
                                                Fecha Entrega
                                            </th>
                                            <th
                                                class="px-2 py-1 text-left border"
                                            >
                                                Cliente
                                            </th>
                                            <th
                                                class="px-2 py-1 text-center border"
                                            >
                                                Estado Entrega
                                            </th>
                                            <th
                                                class="px-2 py-1 text-center border"
                                            >
                                                Estado Pago
                                            </th>
                                            <th
                                                class="px-2 py-1 text-right border"
                                            >
                                                Total
                                            </th>
                                            <th
                                                class="px-2 py-1 text-right border"
                                            >
                                                Pagado
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="order in reference.orders"
                                            :key="order.id"
                                        >
                                            <td
                                                class="px-2 py-1 border text-xs"
                                            >
                                                <a
                                                    :href="`/app/orders/detail/${order.id}`"
                                                    target="_blank"
                                                    rel="noopener"
                                                    class="text-theme-primary hover:underline font-bold"
                                                >
                                                    {{ order.id }}
                                                </a>
                                            </td>
                                            <td
                                                class="px-2 py-1 text-center border text-xs"
                                            >
                                                {{ order.quantity }}
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-xs"
                                            >
                                                {{ order.createdAt | date }}
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-xs"
                                            >
                                                {{ order.deliveryAt | date }}
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-xs"
                                            >
                                                <div
                                                    v-if="order.customer"
                                                    class="flex flex-col gap-1.5 py-1"
                                                >
                                                    <div
                                                        class="flex items-center gap-2"
                                                    >
                                                        <span
                                                            class="text-sm font-medium text-theme-primary"
                                                        >
                                                            {{
                                                                order.customer
                                                                    .firstName
                                                            }}
                                                            {{
                                                                order.customer
                                                                    .lastName
                                                            }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        class="flex items-center gap-1.5"
                                                    >
                                                        <span
                                                            class="text-xs font-medium"
                                                        >
                                                            📱
                                                        </span>
                                                        <span
                                                            class="text-xs font-mono"
                                                        >
                                                            {{
                                                                order.customer
                                                                    .phone
                                                            }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    v-else
                                                    class="flex items-center gap-1.5 py-1"
                                                >
                                                    <span
                                                        class="text-xs italic text-theme-muted bg-theme-hover px-2 py-1 rounded-md"
                                                    >
                                                        Sin cliente asociado
                                                    </span>
                                                </div>
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-center"
                                            >
                                                <span
                                                    :class="
                                                        $data
                                                            .$ordersStatusesMessageClass[
                                                            order.deliveryStatus
                                                        ]
                                                    "
                                                    class="font-medium text-xs"
                                                >
                                                    {{
                                                        $data
                                                            .$ordersStatusesCodes[
                                                            order.deliveryStatus
                                                        ]
                                                    }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-center"
                                            >
                                                <span
                                                    :class="
                                                        $data
                                                            .$paymentStatusesMessageClass[
                                                            order.paymentStatus
                                                        ]
                                                    "
                                                    class="font-medium text-xs"
                                                >
                                                    {{
                                                        $data
                                                            .$paymentStatusesCodes[
                                                            order.paymentStatus
                                                        ]
                                                    }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-right font-semibold"
                                            >
                                                {{ order.total | money }}
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-right font-semibold"
                                                :class="{
                                                    'text-theme-success':
                                                        order.valuePaid >=
                                                        order.total,
                                                    'text-theme-warning':
                                                        order.valuePaid > 0 &&
                                                        order.valuePaid <
                                                            order.total,
                                                    'text-theme-danger':
                                                        order.valuePaid === 0
                                                }"
                                            >
                                                {{ order.valuePaid | money }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div
                                v-else
                                class="text-center text-theme-muted py-4"
                            >
                                No hay órdenes disponibles
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import {
    paymentStatusesCodes,
    paymentStatusesMessageClass
} from "@/common/fields/orders-payment-status-codes";
import {
    ordersStatusesCodes,
    ordersStatusesMessageClass
} from "@/common/fields/orders-status-codes";
import { getNowFormatted } from "@/common/lib/date";
import { downloadExcel } from "@/common/lib/excel";
import {
    generateReferenceOrdersListCompletePDF,
    generateReferenceOrdersListPDF
} from "@/common/lib/pdf";

import OrdersFilters from "@/components/orders/OrdersFilters.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "ReferencesOrder",
    components: {
        AppDialog,
        OrdersFilters,
        CustomTable,
        TooltipButton
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    meta: {
        roles: ["administrator"]
    },
    data: () => ({
        $ordersStatusesCodes: ordersStatusesCodes,
        $ordersStatusesMessageClass: ordersStatusesMessageClass,
        $paymentStatusesCodes: paymentStatusesCodes,
        $paymentStatusesMessageClass: paymentStatusesMessageClass,
        $enabledFilters: [
            "origin",
            "deliveryStatus",
            "paymentStatus",
            "deliveryAt",
            "sellerId"
        ],
        selected: [],
        itemsCurrent: [
            {
                id: 3,
                size: "unico",
                color: "multiple",
                barcode: "",
                priceOffer: 14000.01,
                name: "Figuras armables de Marvel",
                slug: "legos-figuras-marvel",
                sku: "TPM02",
                productId: 3,
                ordersNumber: 2,
                quantity: 2,
                orders: [
                    {
                        total: 10000,
                        valuePaid: 10000,
                        paymentPrice: 0,
                        id: 3,
                        uuid: "176953419645",
                        createdAt: "2026-01-27T17:16:36.495Z",
                        deliveryStatus: "pending",
                        paymentStatus: "approved",
                        deliveryAt: "2026-02-06T05:00:01.000Z",
                        customer: null,
                        quantity: 1
                    },
                    {
                        total: 14000,
                        valuePaid: 14000,
                        paymentPrice: 0,
                        id: 6,
                        uuid: "177090134599",
                        createdAt: "2026-02-12T13:02:25.419Z",
                        deliveryStatus: "pending",
                        paymentStatus: "approved",
                        deliveryAt: "2026-02-13T05:00:01.000Z",
                        customer: {
                            id: "feafa0c0-2761-4c8a-8ab8-a303865ee06e",
                            uid: null,
                            firstName: "Nicolas",
                            lastName: "Pastran",
                            phone: "3022183843"
                        },
                        quantity: 1
                    }
                ]
            }
        ],
        $fields: [
            { key: "id", label: "Ref.", sortable: false },
            { key: "productId", label: "Prod.", sortable: false },
            {
                key: "name",
                label: "Nombre del producto",
                sortable: true
            },
            {
                key: "size",
                label: "Variante",
                sortable: true
            },
            {
                key: "priceOffer",
                label: "Precio actual",
                sortable: true
            },
            {
                key: "quantity",
                label: "Cantidad",
                sortable: true
            },
            {
                key: "ordersNumber",
                label: "# Ordenes",
                sortable: true
            },
            {
                key: "preview",
                label: "Vista previa",
                sortable: false
            }
        ],
        isLoading: false,
        isGeneratingPDF: false,
        isOpenOrdersPreview: false,
        isOpenReferenceSinglePreview: false,
        previewReference: null,
        query: {
            deliveryStatus: ""
        },
        $urlFilterSchema: [
            { key: "origin", type: "string" },
            { key: "deliveryStatus", type: "string" },
            { key: "paymentStatus", type: "string" },
            { key: "deliveryAt", type: "range" },
            { key: "sellerId", type: "number" }
        ]
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission("order:ListReferences", "/app/orders"))
        )
            return;
        this.listItems();
    },
    methods: {
        ...mapActions("orders", ["listNumberOrdersByReferences"]),
        async listItems() {
            this.isLoading = true;
            this.$loading.show();
            try {
                const references = await this.listNumberOrdersByReferences(
                    this.query
                );
                this.itemsCurrent = references;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        reloadItems() {
            this.listItems();
        },
        onRowSelected(items) {
            const currentPageIds = new Set(this.itemsCurrent.map((p) => p.id));
            const itemsMap = new Map();
            this.selected.forEach((item) => {
                itemsMap.set(item.id, item);
            });
            for (const id of currentPageIds) {
                itemsMap.delete(id);
            }

            items.forEach((item) => {
                itemsMap.set(item.id, item);
            });

            this.selected = Array.from(itemsMap.values());
        },
        openPreview() {
            this.isOpenOrdersPreview = true;
        },
        openReferencePreview(item) {
            this.selected = [item]; // solo esa referencia
            this.isOpenOrdersPreview = true; // abre el preview general
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        },
        processItems() {
            const withSelected = this.selected.length > 0;
            const items = withSelected ? this.selected : this.itemsCurrent;
            return {
                items,
                withSelected
            };
        },
        downloadExcelComponent() {
            const { items } = this.processItems();
            const filename = `orders_by_reference_${getNowFormatted(
                "yyyy-MM-dd_HH-mm-ss"
            )}`;
            downloadExcel({
                rows: items.map((item) => ({
                    ...item,
                    orders: JSON.stringify(item.orders)
                })),
                filename
            });
        },
        async downloadPDFComponent() {
            this.isOpenOrdersPreview = false;

            const { items, withSelected } = this.processItems();

            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Tipo de reporte",
                text: `¿Qué tipo de reporte ${
                    withSelected ? "para elementos seleccionados" : ""
                } deseas generar?`,
                icon: "question",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "👀 Reporte Parcial",
                denyButtonText: "📄 Reporte Completo",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#3085d6",
                denyButtonColor: "#28a745",
                cancelButtonColor: "#d33"
            });

            if (dialogResult.isDismissed) {
                return;
            }

            this.isGeneratingPDF = true;
            try {
                if (dialogResult.isConfirmed) {
                    await generateReferenceOrdersListPDF(items, this.query);
                } else {
                    await generateReferenceOrdersListCompletePDF(
                        items,
                        this.query
                    );
                }
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Error al generar PDF",
                    text: error.message || "No se pudo generar el archivo PDF",
                    icon: "error"
                });
            } finally {
                this.isGeneratingPDF = false;
            }
        }
    }
};
</script>
